import React, { createContext, useContext, useReducer, useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost, getToken, setToken, setUnauthorizedHandler } from "./igualdad-api.js";

/* ============================================================
   Talention — Estado de sesion (Context + reducer)
   Raiz del contexto: la EMPRESA activa. De ella cuelgan las politicas
   del registro.
   ============================================================ */

const IgualdadCtx = createContext(null);

const INICIAL = {
  user: null,
  clientes: [],
  cliente: null,
  clienteId: null,
  politicas: [],
  politica: null,
  politicaId: null,
  booting: true,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_USER": return { ...state, user: action.user };
    case "SET_BOOTING": return { ...state, booting: action.booting };
    case "SET_CLIENTES": return { ...state, clientes: action.clientes };
    case "SET_CLIENTE": return { ...state, cliente: action.c || null, clienteId: action.c?.id ?? null };
    case "SET_POLITICAS": return { ...state, politicas: action.politicas };
    case "SET_POLITICA": return { ...state, politica: action.p || null, politicaId: action.p?.id ?? null };
    case "LOGOUT": return { ...INICIAL, booting: false };
    default: return state;
  }
}

/* Persistencia del contexto entre recargas. */
const K_CLIENTE = "ig_cliente_id";
const K_POLITICA = "ig_politica_id";
const leer = (k) => { const v = window.localStorage.getItem(k); return v ? Number(v) : null; };
const guardar = (k, id) => { if (id == null) window.localStorage.removeItem(k); else window.localStorage.setItem(k, String(id)); };

function IgualdadProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INICIAL);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef();
  const ref = useRef(state);
  ref.current = state;

  function fireToast(m) {
    setToast(m);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }

  async function loadClientes() {
    try {
      const res = await apiGet("/clientes", { per_page: 100 });
      const list = (res.data || []).slice().sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
      dispatch({ type: "SET_CLIENTES", clientes: list });
      return list;
    } catch (e) {
      fireToast("No se pudieron cargar las empresas.");
      return [];
    }
  }

  /* Las politicas de una empresa. NO es fatal: un 403 (rol sin politicas.view)
     deja la sesion viva y el modulo de valoracion utilizable. */
  async function loadPoliticasDeCliente(clienteId) {
    try {
      const res = await apiGet("/politicas-retributivas", { per_page: 100 });
      const list = (res.data || [])
        .filter((p) => Number(p.proyecto?.cliente?.id ?? p.proyecto?.cliente_id) === Number(clienteId))
        .slice().sort((a, b) => (b.ejercicio || 0) - (a.ejercicio || 0));
      dispatch({ type: "SET_POLITICAS", politicas: list });
      const guardada = list.find((p) => p.id === leer(K_POLITICA));
      const elegida = guardada || list.find((p) => p.activa) || list[0] || null;

      if (elegida) { setPolitica(elegida); return; }

      /* El registro es imprescindible por dentro, pero elegirlo no es cosa del
         usuario. Si la empresa no tiene ninguno se le pide al backend, que lo
         crea con los valores por defecto. Así se entra en una empresa y se
         trabaja, sin pasar por un desplegable de «política retributiva». */
      const nuevo = await apiGet(`/clientes/${clienteId}/registro-actual`);
      const creada = nuevo?.data || null;
      dispatch({ type: "SET_POLITICAS", politicas: creada ? [creada] : [] });
      setPolitica(creada);
    } catch (e) {
      dispatch({ type: "SET_POLITICAS", politicas: [] });
      setPolitica(null);
    }
  }

  function setPolitica(p) { guardar(K_POLITICA, p?.id ?? null); dispatch({ type: "SET_POLITICA", p }); }
  function setPoliticaById(id) { const p = ref.current.politicas.find((x) => x.id === Number(id)); if (p) setPolitica(p); }

  /* Fija la empresa activa a partir del objeto cliente (no de un id re-leido
     de ref.current, que puede estar obsoleto justo tras cargar la lista en el
     arranque). Al cambiar de empresa desde la UI se limpia la seleccion de
     politica; al restaurar el contexto guardado NO, para conservarla. */
  async function activarCliente(c, limpiarSeleccion = true) {
    guardar(K_CLIENTE, c.id);
    if (limpiarSeleccion) guardar(K_POLITICA, null);
    dispatch({ type: "SET_CLIENTE", c });
    await loadPoliticasDeCliente(c.id);
  }

  async function setClienteById(id) {
    const c = ref.current.clientes.find((x) => x.id === Number(id));
    if (c) await activarCliente(c, true);
  }

  async function login(email, password) {
    const { data } = await apiPost("/auth/login", { email, password });
    setToken(data.access_token);
    dispatch({ type: "SET_USER", user: data.user });
    await loadClientes();
  }

  async function bootstrap() {
    dispatch({ type: "SET_BOOTING", booting: true });
    if (!getToken()) { dispatch({ type: "SET_BOOTING", booting: false }); return; }
    try {
      const me = await apiGet("/auth/me");
      dispatch({ type: "SET_USER", user: me.data });
      const list = await loadClientes();
      const guardado = list.find((c) => c.id === leer(K_CLIENTE));
      if (guardado) await activarCliente(guardado, false);
    } catch (e) {
      setToken(null);
      dispatch({ type: "SET_USER", user: null });
    } finally {
      dispatch({ type: "SET_BOOTING", booting: false });
    }
  }

  function logout() {
    apiPost("/auth/logout").catch(() => {});
    setToken(null);
    guardar(K_CLIENTE, null); guardar(K_POLITICA, null);
    dispatch({ type: "LOGOUT" });
  }

  useEffect(() => {
    setUnauthorizedHandler(() => dispatch({ type: "SET_USER", user: null }));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    if (!ref.current.booting) return;
    bootstrap();
  }, []);

  const value = useMemo(() => ({
    session: state, toast, fireToast,
    login, logout, loadClientes,
    setClienteById, setPolitica, setPoliticaById,
  }), [state, toast]);

  return <IgualdadCtx.Provider value={value}>{children}</IgualdadCtx.Provider>;
}

function useIgualdad() { return useContext(IgualdadCtx); }

/* Comprobacion de permiso sobre la sesion. Es de modulo, no del provider:
   la usan pantallas que ya tienen el usuario a mano y no necesitan el
   contexto entero. Vivia en svpt-metodologia.js hasta la retirada del motor
   de valoracion (spec 2026-09-09). */
function puede(permiso, permisos = [], esStaff = false) {
  return Boolean(esStaff) || (permisos || []).includes(permiso);
}

export { useIgualdad, IgualdadProvider, IgualdadProvider as SessionProvider, puede };
